import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Box,
  Button,
  Text,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  ResponsiveValue,
  BoxProps,
  InputProps,
  ButtonProps,
  TextProps,
  Icon,
  Divider,
  Spinner
} from "@chakra-ui/react";
import { FiSearch as SearchIcon } from '@react-icons/all-files/fi/FiSearch';
import * as CSS from "csstype";

export interface DataProps {
  label: string;
  key: string;
}

export interface SelectInputProps {
  value: string;
  placeholder: string;
  rawData: any[];
  width?: string;
  first?: number;
  onSelect: (element: any | undefined) => void;
  BoxProps?: BoxProps;
  InputProps?: InputProps;
  ButtonProps?: ButtonProps;
  TextProps?: TextProps;
  iconColor?: ResponsiveValue<CSS.Property.BackgroundImage>;
  iconHoverColor?: ResponsiveValue<CSS.Property.BackgroundImage>;
  boxHoverColor?: ResponsiveValue<CSS.Property.BackgroundImage>;
  onChange: (value: string) => void;
  onBlur?: Function;
  isProcessing?: boolean;
}

const renderSource = ({found_in}) => {
  if (found_in.title > 0) {
    return (
      <Box 
        backgroundColor="brand.500" 
        layerStyle="search-badge"
      >
        TITLE
      </Box>
    );
  }

  if (found_in.assets > 0) {
    return (
      <Box 
        backgroundColor="alt.500" 
        layerStyle="search-badge"
      >
        ASSET
      </Box>
    );
  }

  return (
    <Box 
      backgroundColor="text.500" 
      layerStyle="search-badge"
    >
      META DATA
    </Box>
  );
};

export const SelectInput = ({
  value,
  onChange,
  width,
  placeholder,
  rawData,
  onSelect,
  BoxProps,
  ButtonProps,
  InputProps,
  TextProps,
  boxHoverColor,
  first = 50,
  onBlur,
  isProcessing=false
}: SelectInputProps): JSX.Element => {
  const [focusedInput, setFocusedInput] = useState(false);
  const [focusedButton, setFocusedButton] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ref = useRef();

  useEffect(() => {
    if (!ref.current || selectedIndex === -1) return;

    const parent = (ref.current as any);
    const selected = document.getElementById(`search-select-${selectedIndex}`);

    const selectedTop = selected.offsetTop;
    const selectedBottom = selectedTop + selected.offsetHeight;
    const parentTop = parent.scrollTop;
    const parentHeight = parent.offsetHeight;
    const parentBottom = parentTop + parentHeight;

    if (selectedBottom > parentBottom) {
      parent.scrollTop = selectedBottom - parentHeight;
      return;
    }

    if (selectedTop < parentTop) {
      parent.scrollTop = selectedTop;
    }
  }, [selectedIndex, ref]);

  useEffect(() => {
    if (rawData.length === 0) return;

    setSelectedIndex(0);
  }, [rawData])

  const handleChange = (value: string) => {
    onChange(value);
    setSelectedIndex(-1);
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && selectedIndex !== -1) {
      onSelect(rawData[selectedIndex]);
    }

    if (e.key === 'Escape') {
      handleChange('');
    }

    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex(selectedIndex-1);
    }

    const maxPos = Math.min(first-1, rawData.length-1)

    if (e.key === 'ArrowDown' && selectedIndex < maxPos) {
        setSelectedIndex(selectedIndex+1);
    }
  };

  const noResults = !isProcessing && value.length > 2 && rawData.length === 0;
  const hasResults = !isProcessing && !noResults;

  return (
    <Box position="relative" display="inline-block" width={width}>
      <InputGroup
        onFocus={() => setFocusedInput(true)}
        onBlur={() => {
          setTimeout(() => {
            setFocusedInput(false);
            onBlur();
          }, 150);
        }}
      >
        <InputLeftElement>
          <Icon
            boxSize="25px"
            color="brand.500"
            as={SearchIcon}
          />
        </InputLeftElement>
        <Input
          {...InputProps}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e)}
          value={value}
          width="100%"
          autoFocus={true}
        />
        <InputRightElement>
          {value.length > 0 ? (
              <Button 
                variant="unstyled"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={() => handleChange('')}
                color="text.500"
                fontWeight="normal"
                marginRight={10}
              >
                Clear
              </Button>
            ) : null
          }
        </InputRightElement>
      </InputGroup>
      { focusedInput || focusedButton ? (
        <Box
          {...BoxProps}
          position="absolute"
          width="100%"
          maxHeight="200px"
          overflowY="auto"
          marginTop="6px"
          shadow="md"
          rounded="20px"
          zIndex="20"
          onFocus={() => setFocusedButton(true)}
          onBlur={() => setFocusedButton(false)}
          _focus={{ outline: "None" }}
          ref={ref}
        >
          {isProcessing && (
            <Box  
              padding="8px 30px 2px"
              width="100%"
            >
              <Spinner color="brand.500" />
            </Box>
          )}
          {noResults && (
            <Box padding="8px 26px">
              <Text
                fontWeight="normal"
                {...TextProps}
                width="95%"
                textOverflow="ellipsis"
                overflow="hidden"
                fontStyle="italic"
              >
                No results found
              </Text>
            </Box>
          )}
          {hasResults && rawData
            .slice(0, first)
            .map((element, idx) => (
              <Box key={element.id} id={`search-select-${idx}`}>
                <Button
                  _hover={{ backgroundColor: boxHoverColor }}
                  {...ButtonProps}
                  variant="ghost"
                  width="100%"
                  textAlign="left"
                  backgroundColor={idx === selectedIndex ? '#E2F5F5' : 'inherit'}
                  onClick={() => {
                    setFocusedButton(false);
                    setFocusedInput(false);
                    onSelect(element);
                  }}
                >
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Box flex="1 1 auto" minWidth="1px">
                      <Text
                        fontWeight="normal"
                        {...TextProps}
                        paddingLeft="10px"
                        width="95%"
                        textOverflow="ellipsis"
                        overflow="hidden"
                      >
                        {element.title}
                      </Text>
                    </Box>
                    <Box 
                      marginRight="10px" 
                      flex="0 0 80px" 
                      textAlign="center" 
                      fontWeight="normal" 
                      color="var(--color-white)"
                      fontSize={12}
                    >
                      {renderSource(element)}
                    </Box>
                  </Box>
                </Button>
                <Divider />
              </Box>
            ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default SelectInput;